import { FOCUS_RING, Icon } from '@amelio/ui-library';

import AppLink from '@/components/AppLink/AppLink';

interface BackLinkProps {
    /** Text shown next to the pill, e.g. "Retour aux objectifs". */
    label: string;
    /** Imperative navigation (history.push/goBack) -- the way every current caller navigates. */
    onClick?: () => void;
    /** Declarative navigation: renders a real link (`AppLink`) instead of a `button`. */
    to?: string;
    /** Button form only: blocks the navigation while the page is busy, e.g. saving. */
    disabled?: boolean;
    className?: string;
    dataTestId?: string;
}

/**
 * The app-wide "back" affordance: a round translucent pill around a chevron, then a label.
 * Ported from the Objectives module's `ObjectiveBackLink` (P16-3, ADO #15449) so every page that
 * needs a back link -- Objectives included, pillar page included -- renders the same appearance;
 * Maxime: "il faut uniformiser l'app quand ça arrive."
 *
 * The prototype (`performance-ai6`) draws this pill with two different glyphs depending on the
 * page: `ArrowLeft` on the pillar page's plain-text link (no pill there), and `ChevronLeft` inside
 * the round pill everywhere else (detail, type-choice, the form). Since the pill itself only ever
 * appears with the chevron in ai6, this component keeps that one glyph (`arrow-arrow-left-1`) and
 * drops the arrow variant the codebase used to carry for the pillar page's old, pill-less link.
 */
export default function BackLink({
    label,
    onClick,
    to,
    disabled,
    className,
    dataTestId,
}: BackLinkProps) {
    const sharedClassName = `${FOCUS_RING} text-navy flex w-fit items-center gap-2 transition-colors transition-opacity hover:opacity-70${
        className ? ` ${className}` : ''
    }`;

    const content = (
        <>
            {/* The pill is a plain visual insert around the glyph -- fond, padding and rayon live
                here, not on a container around the interactive element -- while the whole row
                (pill, gap and label) stays inside the single `button`/`a` below, so every part of
                it is clickable, exactly like the label text next to it. */}
            <span className={'flex h-8 w-8 items-center justify-center rounded-full bg-white/45'}>
                <Icon
                    name={'arrow-arrow-left-1'}
                    size={'md'}
                    color={'inherit'}
                />
            </span>

            <span className={'text-sm font-medium'}>{label}</span>
        </>
    );

    if (to) {
        return (
            <AppLink
                to={to}
                onClick={onClick}
                className={sharedClassName}
                dataTestId={dataTestId}
            >
                {content}
            </AppLink>
        );
    }

    return (
        <button
            type={'button'}
            onClick={onClick}
            disabled={disabled}
            className={sharedClassName}
            data-testid={dataTestId}
        >
            {content}
        </button>
    );
}
